import {
  cacheKeys,
  FeatureName,
  useDeleteMutation,
  useIsFeatureEnabled,
} from '@gripp/shared-logic';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Colors } from '../../themes/colors';
import { Button } from '../crop-circle/Button';
import { tokens } from '../crop-circle/tokens';
import LoadingIndicator from '../loadingIndicator';

type DeleteIssueProps = {
  issueId: string;
  onDeleted: () => Promise<void>;
};

export const DeleteIssue = (props: DeleteIssueProps) => {
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  if (!isCropCircleEnabled) return null;

  return <DeleteIssueBase {...props} />;
};

const DeleteIssueBase = (props: DeleteIssueProps) => {
  const [showAlert, setShowAlert] = useState(false);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useDeleteMutation({
    modelName: 'issue',
    query: DELETE_ISSUE,
  });

  const onDelete = async () => {
    if (isPending) return;

    await mutateAsync(
      { input: { id: props.issueId } },
      {
        onSuccess: async () => {
          const cacheKey = cacheKeys('workspaceasset').all;
          await queryClient.invalidateQueries({ queryKey: cacheKey });
          await props.onDeleted();
        },
      }
    );
  };

  return (
    <>
      <Button
        variant="rectangular"
        state="outline"
        onPress={() => setShowAlert(true)}
        style={styles.deleteButton}
      >
        {t('issue.delete.title')}
      </Button>
      <Modal
        visible={showAlert}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAlert(false)}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalContainer}>
            <Text style={styles.modalTitle}>{t('issue.delete.title')}</Text>
            <Text style={styles.modalMessage}>{t('issue.delete.warning')}</Text>
            <Button
              variant="rectangular"
              state="warning"
              onPress={onDelete}
              style={styles.buttons}
              leftIcon={
                isPending && (
                  <LoadingIndicator
                    size={16}
                    backgroundColor={tokens.colors.orange[500]}
                  />
                )
              }
            >
              {t('issue.menu.delete')}
            </Button>
            <Button
              variant="rectangular"
              state="default"
              onPress={() => setShowAlert(false)}
              style={styles.buttons}
            >
              {t('issue.delete.cancel')}
            </Button>
          </View>
        </View>
      </Modal>
    </>
  );
};

const DELETE_ISSUE = `
mutation deleteIssue($input: MutateIssueInput!) {
  deleteIssue(input: $input) {
    id
  }
}
`;

const styles = StyleSheet.create({
  modalBackdrop: {
    backgroundColor: Colors.blackInactive,
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: Colors.white,
    padding: 16,
    position: 'absolute',
    right: 0,
    bottom: 0,
    left: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.black,
    textAlign: 'center',
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 14,
    fontWeight: '400',
    color: Colors.grayText,
    textAlign: 'center',
    marginBottom: 16,
  },
  buttons: {
    marginBottom: 12,
    width: '100%',
    height: 48,
  },
  deleteButton: {
    marginBottom: 12,
    width: '100%',
    height: 48,
  },
});
